import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { ModalWithHeaderDialog } from '@ingenico/react-ui/confirmation-dialog';
import { SvgAsset } from '@ingenico/react-ui/svg-asset';
import type { ForwardedRef, ReactElement } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TemConnectionModalBody } from '../tem-connection-modal-body/TemConnectionModalBody.ctrl';
import { TemConnectionModalHeader } from '../tem-connection-modal-header/TemConnectionModalHeader';
import type { ModalScreenShareApi, ModalScreenShareProps } from './modalScreenShare.model';
import styles from './modalScreenShare.module.scss';
import { useModalScreenShare } from './useModalScreenShare.vm';

function ModalScreenShareComponent(
  {
    onClose,
    onConnectionSucceed,
    isConnecting,
    isFailure,
    isNotAllowed,
    isSuccess,
    triggerSignatureDevicePermissionCall,
    progressInPercent,
    isRefusedByUser,
  }: ModalScreenShareProps,
  ref: ForwardedRef<ModalScreenShareApi>,
): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE, ALL_LNGS_NAMESPACES.COMMON]);

  const { isModalSuccess, screenShareData, isModalError, handleCloseModal, handleConfirmModal, isModalLoading } =
    useModalScreenShare({
      ref,
      onClose,
      onConnectionSucceed,
      isConnecting,
      isFailure,
      isNotAllowed,
      isSuccess,
      triggerSignatureDevicePermissionCall,
      isRefusedByUser,
    });

  return (
    <ModalWithHeaderDialog
      progressInPercent={progressInPercent}
      cancelButtonDisabled={isModalSuccess}
      caption={t('confirmation.screenShare.subTitle')}
      confirmButtonStartIcon={<SvgAsset className={styles.confirmIcon} image="restart" />}
      confirmLabel={t('button.retry', { ns: ALL_LNGS_NAMESPACES.COMMON })}
      content={<TemConnectionModalBody step={screenShareData.status} />}
      customClassnames={{ headerClassName: styles.headerClassName }}
      hasCloseIcon
      header={<TemConnectionModalHeader />}
      hideConfirmButton={!isModalError}
      i18nNamespaces={[ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]}
      isLoading={isModalLoading}
      isOpen={screenShareData.open}
      onClose={handleCloseModal}
      onConfirm={handleConfirmModal}
      title={t('confirmation.screenShare.title')}
      allowClickOutside={false}
      allowCloseByEscapeKey={false}
    />
  );
}

export const ModalScreenShare = forwardRef(ModalScreenShareComponent);
